export const CHAIN_STEP_TYPES = ['video', 'free', 'iap', 'chest'] as const;
export type ChainStepType = typeof CHAIN_STEP_TYPES[number];

export const CHAIN_GRANT_TYPES = ['BETCOINS', 'DAILY_BONUS_MULTIPLIER'] as const;
export type ChainGrantType = typeof CHAIN_GRANT_TYPES[number];

export const CHAIN_HOME_SLOT_MODES = [
    'chain_if_assigned_else_video',
    'chain_replaces_video',
    'feature_flag',
] as const;

export interface ChainGrant {
    type: ChainGrantType;
    amount?: number | null;
    coef?: number | null;
    days?: number | null;
}

export interface ChainStep {
    type: ChainStepType;
    coins: number;
    placementId?: string | null;
    storeCode?: string | null;
    packName?: string | null;
    priceLabel?: string | null;
    extraLabel?: string | null;
    grants: ChainGrant[];
}

export interface ChainDefinition {
    id: string;
    name: string;
    durationHours: number;
    onExpire: 'reset_progress' | 'close_and_reassign' | 'keep_claimed_freeze_ui';
    reassignPolicy: 'after_duration' | 'cooldown_hours' | 'next_calendar_day';
    reassignCooldownHours: number;
    steps: ChainStep[];
}

export interface ChainAudienceRule {
    id: string;
    enabled: boolean;
    includeTags: string[];
    excludeTags: string[];
    chainId: string;
}

export interface ChainOfferExperiment {
    enabled: boolean;
    holdoutPercent: number;
    salt: string;
}

export interface ChainOfferDocument {
    version: number;
    globalEnabled: boolean;
    experiment: ChainOfferExperiment;
    defaultAudienceId: string;
    homeSlotMode: typeof CHAIN_HOME_SLOT_MODES[number] | string;
    presentation: 'SHEET' | 'DIALOG' | 'TRAIL' | string;
    celebrateMotion: boolean;
    videoUnavailableBehavior: 'disable_cta' | 'hide_tile' | 'show_retry' | string;
    timezone: string;
    chains: ChainDefinition[];
    audiences: ChainAudienceRule[];
}

export interface ChainOfferBundleDto {
    config: ChainOfferDocument;
    persisted: boolean;
    canRollback: boolean;
    warnings: string[];
}

export interface ChainOfferPreviewDto {
    betupId: number;
    found: boolean;
    tags: string[];
    audienceId?: string | null;
    chainId?: string | null;
    group?: string | null;
    stepIndex?: number | null;
    status?: string | null;
    summary: string;
}

export function emptyVideoStep(): ChainStep {
    return {
        type: 'video',
        coins: 1000,
        placementId: 'chain_video',
        grants: [{ type: 'BETCOINS', amount: 1000 }],
    };
}

export function emptyFreeStep(coins = 2000): ChainStep {
    return {
        type: 'free',
        coins,
        grants: [{ type: 'BETCOINS', amount: coins }],
    };
}

export function emptyIapStep(): ChainStep {
    return {
        type: 'iap',
        coins: 250000,
        storeCode: 'starter_pack',
        packName: 'Starter pack',
        priceLabel: '$0.99',
        grants: [{ type: 'BETCOINS', amount: 250000 }],
    };
}

export function emptyChestStep(): ChainStep {
    return {
        type: 'chest',
        coins: 10000,
        extraLabel: 'Daily bonus ×2 for 3 days',
        grants: [
            { type: 'BETCOINS', amount: 10000 },
            { type: 'DAILY_BONUS_MULTIPLIER', coef: 2, days: 3 },
        ],
    };
}

export function uniqueChainId(base: string, existing: string[]): string {
    const taken = new Set(existing.map(id => id.toLowerCase()));
    const slug = base.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_|_$/g, '').slice(0, 64) || 'chain';
    if (!taken.has(slug)) return slug;
    let n = 2;
    while (taken.has(`${slug}_${n}`)) n += 1;
    return `${slug}_${n}`;
}
